import { useState, useCallback } from "react";

export type Coords = { lat: number; lng: number; accuracy: number };

/**
 * Best-effort browser geolocation. `locate()` triggers the permission prompt and,
 * on success, exposes the device coordinates. Callers decide when to read `coords`
 * (e.g. to enrich a request context); failures are surfaced via `error` and never
 * throw, so consumers can treat location as optional.
 */
export function useGeolocation() {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const locate = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("not_supported");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy, // metres
        });
        setLoading(false);
      },
      (err) => {
        setError(
          err.code === err.PERMISSION_DENIED ? "denied" : "unavailable",
        );
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }, []);

  return { coords, error, loading, locate };
}
